import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { clubAPI } from '../services/api';
import './ClubEdit.css';

function ClubEdit() {
  const { clubId } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [club, setClub] = useState(null);
  const [eligibleCaptains, setEligibleCaptains] = useState([]);
  const [formData, setFormData] = useState({});
  const [activeSection, setActiveSection] = useState('general');
  const [theme] = useState(localStorage.getItem('theme') || 'light');
  const [reloading, setReloading] = useState(false);
  const [reloadResult, setReloadResult] = useState(null);

  useEffect(() => {
    document.documentElement.setAttribute('data-theme', theme);
  }, [theme]);

  useEffect(() => {
    loadClubData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [clubId]);

  const loadClubData = async () => {
    try {
      setLoading(true);
      const data = await clubAPI.getClubEditData(clubId);
      if (data.success) {
        setClub(data.club);
        setFormData(data.club);
        setEligibleCaptains(data.eligibleCaptains);
      } else {
        setError(data.error || 'Failed to load club data');
      }
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to load club data');
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    setError('');
    setSuccess('');

    try {
      const data = await clubAPI.updateClub(clubId, formData);
      if (data.success) {
        setSuccess('Club settings saved successfully');
        setTimeout(() => setSuccess(''), 3000);
      } else {
        setError(data.error || 'Failed to save settings');
      }
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to save settings');
    } finally {
      setSaving(false);
    }
  };

  const handlePauseToggle = async () => {
    try {
      const data = await clubAPI.togglePauseClub(clubId);
      if (data.success) {
        setFormData(prev => ({ ...prev, isPaused: data.isPaused }));
        setSuccess(data.message);
        setTimeout(() => setSuccess(''), 3000);
      }
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to toggle pause');
    }
  };

  const daysOfWeek = [
    { value: 0, label: 'Sunday' },
    { value: 1, label: 'Monday' },
    { value: 2, label: 'Tuesday' },
    { value: 3, label: 'Wednesday' },
    { value: 4, label: 'Thursday' },
    { value: 5, label: 'Friday' },
    { value: 6, label: 'Saturday' },
  ];

  const frequencyOptions = [
    { value: 'weekly', label: 'Weekly' },
    { value: 'daily', label: 'Daily' },
  ];

  const amountTypeOptions = [
    { value: 'percent', label: 'Percentage of kitty' },
    { value: 'dollars', label: 'Fixed dollar amount' },
  ];

  const contributionFrequencyOptions = [
    { value: 'weekly', label: 'Weekly' },
    { value: 'fortnightly', label: 'Fortnightly' },
    { value: 'monthly', label: 'Monthly' },
  ];

  const membershipStateOptions = [
    { value: 1, label: 'Public - Anyone with link can join' },
    { value: 2, label: 'Captain Invite Only' },
    { value: 3, label: 'Member Invite Only' },
    { value: 4, label: 'Closed - No new members' },
  ];

  const leaderboardTypeOptions = [
    { value: 'all_members', label: 'All Members' },
    { value: 'punters_only', label: 'Punters Only' },
  ];

  if (loading) {
    return (
      <div className="club-edit-loading">
        <i className="fas fa-spinner fa-spin"></i>
        <p>Loading club settings...</p>
      </div>
    );
  }

  if (error && !club) {
    return (
      <div className="club-edit-error">
        <i className="fas fa-exclamation-triangle"></i>
        <p>{error}</p>
        <button onClick={() => navigate(-1)}>Go Back</button>
      </div>
    );
  }

  return (
    <div className="club-edit">
      <h2>Manage Club Settings</h2>

      {error && <div className="alert alert-error">{error}</div>}
      {success && <div className="alert alert-success">{success}</div>}

      <div className="club-edit-layout">
        <nav className="club-edit-nav">
          <button
            className={activeSection === 'general' ? 'active' : ''}
            onClick={() => setActiveSection('general')}
          >
            <i className="fas fa-cog"></i> General Settings
          </button>
          <button
            className={activeSection === 'betting' ? 'active' : ''}
            onClick={() => setActiveSection('betting')}
          >
            <i className="fas fa-dice"></i> Betting Settings
          </button>
          <button
            className={activeSection === 'contributions' ? 'active' : ''}
            onClick={() => setActiveSection('contributions')}
          >
            <i className="fas fa-dollar-sign"></i> Contributions
          </button>
          <button
            className={activeSection === 'wall' ? 'active' : ''}
            onClick={() => setActiveSection('wall')}
          >
            <i className="fas fa-comments"></i> Wall Settings
          </button>
          <button
            className={activeSection === 'membership' ? 'active' : ''}
            onClick={() => setActiveSection('membership')}
          >
            <i className="fas fa-users"></i> Membership
          </button>
          <button
            className={activeSection === 'management' ? 'active' : ''}
            onClick={() => setActiveSection('management')}
          >
            <i className="fas fa-tools"></i> Management Tools
          </button>
        </nav>

        <form className="club-edit-form" onSubmit={handleSubmit}>
          {/* General Settings */}
          {activeSection === 'general' && (
            <section className="form-section">
              <h2>General Settings</h2>

              <div className="form-group">
                <label htmlFor="name">Club Name</label>
                <input
                  type="text"
                  id="name"
                  name="name"
                  value={formData.name || ''}
                  onChange={handleInputChange}
                  maxLength={100}
                />
              </div>

              <div className="form-group">
                <label htmlFor="description">Description</label>
                <textarea
                  id="description"
                  name="description"
                  value={formData.description || ''}
                  onChange={handleInputChange}
                  rows={4}
                  placeholder="Describe your club..."
                />
              </div>

              <div className="form-group">
                <label htmlFor="captainUserId">Captain</label>
                <select
                  id="captainUserId"
                  name="captainUserId"
                  value={formData.captain?.userId || ''}
                  onChange={handleInputChange}
                >
                  {eligibleCaptains.map(member => (
                    <option key={member.userId} value={member.userId}>
                      {member.name} ({member.email})
                    </option>
                  ))}
                </select>
                <p className="help-text">Changing the captain will transfer all captain privileges to the selected member.</p>
              </div>

              <div className="form-group">
                <label htmlFor="dayOfTheWeek">Rollover Day</label>
                <select
                  id="dayOfTheWeek"
                  name="dayOfTheWeek"
                  value={formData.dayOfTheWeek ?? ''}
                  onChange={handleInputChange}
                >
                  {daysOfWeek.map(day => (
                    <option key={day.value} value={day.value}>{day.label}</option>
                  ))}
                </select>
                <p className="help-text">The day when punting period resets.</p>
              </div>
            </section>
          )}

          {/* Betting Settings */}
          {activeSection === 'betting' && (
            <section className="form-section">
              <h2>Betting Settings</h2>

              <div className="form-group">
                <label htmlFor="betFrequency">Betting Frequency</label>
                <select
                  id="betFrequency"
                  name="betFrequency"
                  value={formData.betFrequency || 'weekly'}
                  onChange={handleInputChange}
                >
                  {frequencyOptions.map(opt => (
                    <option key={opt.value} value={opt.value}>{opt.label}</option>
                  ))}
                </select>
              </div>

              <div className="form-group">
                <label htmlFor="betAmountType">Bet Amount Type</label>
                <select
                  id="betAmountType"
                  name="betAmountType"
                  value={formData.betAmountType || 'percent'}
                  onChange={handleInputChange}
                >
                  {amountTypeOptions.map(opt => (
                    <option key={opt.value} value={opt.value}>{opt.label}</option>
                  ))}
                </select>
              </div>

              <div className="form-group">
                <label htmlFor="betPercentage">
                  {formData.betAmountType === 'percent' ? 'Bet Percentage' : 'Bet Amount ($)'}
                </label>
                <input
                  type="number"
                  id="betPercentage"
                  name="betPercentage"
                  value={formData.betPercentage || ''}
                  onChange={handleInputChange}
                  min={1}
                  max={formData.betAmountType === 'percent' ? 100 : 10000}
                  step={formData.betAmountType === 'percent' ? 1 : 0.01}
                />
                <p className="help-text">
                  {formData.betAmountType === 'percent'
                    ? 'Percentage of kitty to bet each period'
                    : 'Fixed dollar amount to bet each period'}
                </p>
              </div>

              <div className="form-group">
                <label htmlFor="puntersPerPeriod">Punters Per Period</label>
                <select
                  id="puntersPerPeriod"
                  name="puntersPerPeriod"
                  value={formData.puntersPerPeriod || '1'}
                  onChange={handleInputChange}
                >
                  {[1, 2, 3, 4, 5, 6, 7, 8, 9, 10].map(num => (
                    <option key={num} value={num}>{num}</option>
                  ))}
                </select>
                <p className="help-text">Number of members who can punt each period.</p>
              </div>

              <div className="form-group">
                <label htmlFor="minimumRoi">Minimum ROI Target (%)</label>
                <input
                  type="number"
                  id="minimumRoi"
                  name="minimumRoi"
                  value={formData.minimumRoi || 200}
                  onChange={handleInputChange}
                  min={0}
                />
                <p className="help-text">ROI target for punters to reach.</p>
              </div>
            </section>
          )}

          {/* Contributions */}
          {activeSection === 'contributions' && (
            <section className="form-section">
              <h2>Contribution Settings</h2>

              <div className="form-group">
                <label htmlFor="contributionAmount">Contribution Amount ($)</label>
                <input
                  type="number"
                  id="contributionAmount"
                  name="contributionAmount"
                  value={formData.contributionAmount || ''}
                  onChange={handleInputChange}
                  min={0}
                  step={1}
                />
              </div>

              <div className="form-group">
                <label htmlFor="contributionFrequency">Contribution Frequency</label>
                <select
                  id="contributionFrequency"
                  name="contributionFrequency"
                  value={formData.contributionFrequency || 'weekly'}
                  onChange={handleInputChange}
                >
                  {contributionFrequencyOptions.map(opt => (
                    <option key={opt.value} value={opt.value}>{opt.label}</option>
                  ))}
                </select>
              </div>

              <div className="form-group">
                <label htmlFor="kittyReserve">Kitty Reserve ($)</label>
                <input
                  type="number"
                  id="kittyReserve"
                  name="kittyReserve"
                  value={formData.kittyReserve || 0}
                  onChange={handleInputChange}
                  min={0}
                  step={1}
                />
                <p className="help-text">Minimum amount to keep in the kitty after withdrawals.</p>
              </div>
            </section>
          )}

          {/* Wall Settings */}
          {activeSection === 'wall' && (
            <section className="form-section">
              <h2>Wall Settings</h2>

              <div className="form-group checkbox-group">
                <label>
                  <input
                    type="checkbox"
                    name="allowWallPosts"
                    checked={formData.allowWallPosts ?? true}
                    onChange={handleInputChange}
                  />
                  <span>Allow members to post on the wall</span>
                </label>
              </div>

              <div className="form-group checkbox-group">
                <label>
                  <input
                    type="checkbox"
                    name="allowComments"
                    checked={formData.allowComments ?? true}
                    onChange={handleInputChange}
                  />
                  <span>Allow comments on wall posts</span>
                </label>
              </div>

              <div className="form-group checkbox-group">
                <label>
                  <input
                    type="checkbox"
                    name="isRobotEnabled"
                    checked={formData.isRobotEnabled ?? true}
                    onChange={handleInputChange}
                  />
                  <span>Enable robot posts (daily summaries)</span>
                </label>
              </div>

              <div className="form-group">
                <label htmlFor="leaderboardType">Leaderboard Type</label>
                <select
                  id="leaderboardType"
                  name="leaderboardType"
                  value={formData.leaderboardType || 'all_members'}
                  onChange={handleInputChange}
                >
                  {leaderboardTypeOptions.map(opt => (
                    <option key={opt.value} value={opt.value}>{opt.label}</option>
                  ))}
                </select>
              </div>
            </section>
          )}

          {/* Membership */}
          {activeSection === 'membership' && (
            <section className="form-section">
              <h2>Membership Settings</h2>

              <div className="form-group">
                <label htmlFor="membershipState">Membership Status</label>
                <select
                  id="membershipState"
                  name="membershipState"
                  value={formData.membershipState || 1}
                  onChange={handleInputChange}
                >
                  {membershipStateOptions.map(opt => (
                    <option key={opt.value} value={opt.value}>{opt.label}</option>
                  ))}
                </select>
              </div>

              <div className="form-group">
                <label htmlFor="memberCap">Member Cap</label>
                <input
                  type="number"
                  id="memberCap"
                  name="memberCap"
                  value={formData.memberCap || ''}
                  onChange={handleInputChange}
                  min={0}
                  placeholder="No limit"
                />
                <p className="help-text">Maximum number of members allowed (0 or blank for unlimited).</p>
              </div>

              <div className="form-group checkbox-group">
                <label>
                  <input
                    type="checkbox"
                    name="smsNotifications"
                    checked={formData.smsNotifications ?? false}
                    onChange={handleInputChange}
                  />
                  <span>Enable SMS notifications (charges apply)</span>
                </label>
                <p className="help-text">SMS notifications cost 20c per text per member.</p>
              </div>
            </section>
          )}

          {/* Management Tools */}
          {activeSection === 'management' && (
            <section className="form-section">
              <h2>Management Tools</h2>

              <div className="management-tools">
                <div className="tool-card">
                  <h3>
                    <i className={`fas ${formData.isPaused ? 'fa-play' : 'fa-pause'}`}></i>
                    {formData.isPaused ? 'Unpause Club' : 'Pause Club'}
                  </h3>
                  <p>
                    {formData.isPaused
                      ? 'Club is currently paused. Members cannot bet while paused.'
                      : 'Pausing the club will temporarily stop all betting activity.'}
                  </p>
                  <button
                    type="button"
                    className={`tool-btn ${formData.isPaused ? 'success' : 'warning'}`}
                    onClick={handlePauseToggle}
                  >
                    {formData.isPaused ? 'Unpause Club' : 'Pause Club'}
                  </button>
                </div>

                <div className="tool-card">
                  <h3><i className="fas fa-redo"></i> Reset Leaderboard</h3>
                  <p>Reset all member ROI stats to 0%. This cannot be undone.</p>
                  <button type="button" className="tool-btn danger" disabled>
                    Reset Leaderboard
                  </button>
                </div>

                <div className="tool-card">
                  <h3><i className="fas fa-coins"></i> Reset Contributions</h3>
                  <p>Reset current contribution counters. Lifetime totals are preserved.</p>
                  <button type="button" className="tool-btn danger" disabled>
                    Reset Contributions
                  </button>
                </div>

                <div className="tool-card">
                  <h3><i className="fas fa-sync"></i> Reload Betting Account</h3>
                  <p>Returns current betting balance to kitty, then loads the club's assigned bet amount back into betting.</p>
                  {reloadResult && (
                    <div className={`tool-result ${reloadResult.success ? 'success' : 'error'}`}>
                      {reloadResult.success ? reloadResult.message : reloadResult.error}
                    </div>
                  )}
                  <button
                    type="button"
                    className="tool-btn primary"
                    disabled={reloading}
                    onClick={async () => {
                      setReloading(true);
                      setReloadResult(null);
                      try {
                        const data = await clubAPI.reloadBettingAccount(clubId);
                        if (data.success) {
                          setReloadResult({ success: true, message: data.message });
                        } else {
                          setReloadResult({ success: false, error: data.error || 'Reload failed' });
                        }
                      } catch (err) {
                        setReloadResult({ success: false, error: err.response?.data?.error || 'Reload failed' });
                      } finally {
                        setReloading(false);
                      }
                    }}
                  >
                    {reloading ? <><i className="fas fa-spinner fa-spin"></i> Reloading...</> : 'Reload Betting Account'}
                  </button>
                </div>
              </div>
            </section>
          )}

          {activeSection !== 'management' && (
            <div className="form-actions">
              <button type="button" className="btn-cancel" onClick={() => navigate(`/dashboard/${clubId}`)}>
                Cancel
              </button>
              <button type="submit" className="btn-save" disabled={saving}>
                {saving ? 'Saving...' : 'Save Changes'}
              </button>
            </div>
          )}
        </form>
      </div>
    </div>
  );
}

export default ClubEdit;
